import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { routesAPI, usersAPI } from '@/lib/api';
import { formatDate, getStatusColor, getTodayString } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  Loader2, 
  Calendar, 
  Users, 
  Truck, 
  ArrowRight, 
  Plus,
  Sparkles,
  CheckCircle,
  AlertTriangle,
  Clock,
  ChevronDown,
  ChevronUp,
  Gauge,
  UserX,
  TrendingUp,
  Trash2,
  Scissors,
  Edit2,
  X
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export default function AdminRoutes() {
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [dateFilter, setDateFilter] = useState(getTodayString());
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Recommendations state
  const [recommendations, setRecommendations] = useState(null);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(true);
  const [acceptingRoute, setAcceptingRoute] = useState(null);
  const [editingRecIdx, setEditingRecIdx] = useState(null);  // Index of recommendation being edited
  
  const [formData, setFormData] = useState({
    name: '',
    route_date: getTodayString(),
    driver_id: 'unassigned',
    notes: '',
  });

  // Calculate tomorrow's date
  const getTomorrowString = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  useEffect(() => {
    fetchRoutes();
    fetchDrivers();
  }, [dateFilter]);

  // Auto-fetch recommendations for tomorrow on mount
  useEffect(() => {
    fetchRecommendations(getTomorrowString());
  }, []);

  const fetchRoutes = async () => {
    setLoading(true);
    try {
      const response = await routesAPI.getAll(dateFilter || undefined);
      setRoutes(response.data);
    } catch (error) {
      console.error('Error fetching routes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await usersAPI.getAll('driver');
      setDrivers(response.data);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchRecommendations = async (date) => {
    setLoadingRecs(true);
    try {
      const response = await routesAPI.getRecommendations(date);
      setRecommendations(response.data);
      
      // Show feedback if no recommendations
      if (!response.data.recommendations?.length) {
        if (response.data.summary?.drivers_available === 0) {
          toast.info(`No available drivers for ${date}. All drivers have routes or are off.`);
        } else if (response.data.unassigned_stops?.length === 0) {
          toast.info(`No service needs detected for ${date}. Try a date further out.`);
        }
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      setRecommendations(null);
      toast.error('Failed to generate recommendations');
    } finally {
      setLoadingRecs(false);
    }
  };

  const handleAcceptRecommendation = async (rec) => {
    setAcceptingRoute(rec.driver_id);
    try {
      const response = await routesAPI.acceptRecommendation(rec);
      
      // Build success message including auto-assigned units
      let message = `Route "${rec.name}" created with ${rec.total_stops} stops!`;
      if (response.data?.units_auto_assigned > 0) {
        message = `Route "${rec.name}" created with ${rec.total_stops} stops. ${response.data.units_auto_assigned} units auto-assigned to deliveries!`;
      }
      toast.success(message);
      
      // Refresh both routes and recommendations
      fetchRoutes();
      fetchRecommendations(getTomorrowString());
    } catch (error) {
      console.error('Error accepting recommendation:', error);
      toast.error('Failed to create route');
    } finally {
      setAcceptingRoute(null);
    }
  };

  const handleAcceptAll = async () => {
    if (!recommendations?.recommendations?.length) return;
    
    for (const rec of recommendations.recommendations) {
      await handleAcceptRecommendation(rec);
    }
  };

  // Remove a stop from a recommendation (before accepting)
  const removeStopFromRecommendation = (recIdx, stopIdx) => {
    setRecommendations(prev => {
      if (!prev) return prev;
      
      const newRecs = { ...prev };
      const updatedRecommendations = [...newRecs.recommendations];
      const rec = { ...updatedRecommendations[recIdx] };
      
      // Remove the stop
      rec.stops = rec.stops.filter((_, i) => i !== stopIdx);
      rec.total_stops = rec.stops.length;
      
      // Recalculate estimated time (rough estimate)
      const totalMinutes = rec.stops.reduce((sum, stop) => {
        const baseTime = 5;
        const perUnit = stop.job_type === 'service' ? 7 : 8;
        return sum + baseTime + (perUnit * (stop.units_requested || 1)) + 12; // +12 for drive time
      }, 0);
      
      const hours = Math.floor(totalMinutes / 60);
      const mins = totalMinutes % 60;
      rec.estimated_time = {
        ...rec.estimated_time,
        total_minutes: totalMinutes,
        formatted: hours > 0 ? `${hours}h ${mins}m` : `${mins} min`
      };
      
      updatedRecommendations[recIdx] = rec;
      
      // If no stops left, remove the recommendation entirely
      if (rec.stops.length === 0) {
        updatedRecommendations.splice(recIdx, 1);
        toast.info('Recommendation removed (no stops remaining)');
      }
      
      newRecs.recommendations = updatedRecommendations;
      newRecs.summary = {
        ...newRecs.summary,
        total_stops: updatedRecommendations.reduce((sum, r) => sum + r.total_stops, 0),
        total_recommended_routes: updatedRecommendations.length
      };
      
      return newRecs;
    });
    
    toast.success('Stop removed from recommendation');
  };

  // Split a recommendation into two at a specific index
  const splitRecommendation = (recIdx, splitAfterIdx) => {
    setRecommendations(prev => {
      if (!prev) return prev;
      
      const newRecs = { ...prev };
      const updatedRecommendations = [...newRecs.recommendations];
      const originalRec = updatedRecommendations[recIdx];
      
      if (originalRec.stops.length < 2) {
        toast.error('Cannot split a route with less than 2 stops');
        return prev;
      }
      
      // Split the stops
      const firstHalfStops = originalRec.stops.slice(0, splitAfterIdx + 1);
      const secondHalfStops = originalRec.stops.slice(splitAfterIdx + 1);
      
      if (secondHalfStops.length === 0) {
        toast.error('No stops to split off');
        return prev;
      }
      
      // Update original with first half
      const firstRoute = {
        ...originalRec,
        id: originalRec.id,
        name: `${originalRec.territory} - Part 1`,
        stops: firstHalfStops,
        total_stops: firstHalfStops.length
      };
      
      // Create second route (unassigned driver)
      const secondRoute = {
        ...originalRec,
        id: `${originalRec.id}-split`,
        name: `${originalRec.territory} - Part 2`,
        driver_id: null,
        driver_name: null,
        stops: secondHalfStops,
        total_stops: secondHalfStops.length
      };
      
      // Replace original and add new
      updatedRecommendations[recIdx] = firstRoute;
      updatedRecommendations.splice(recIdx + 1, 0, secondRoute);
      
      newRecs.recommendations = updatedRecommendations;
      newRecs.summary = {
        ...newRecs.summary,
        total_recommended_routes: updatedRecommendations.length
      };
      
      return newRecs;
    });
    
    toast.success('Route split into two parts');
    setEditingRecIdx(null);
  };

  const handleCreateRoute = async () => {
    if (!formData.name || !formData.route_date) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      const driverId = formData.driver_id === 'unassigned' ? null : formData.driver_id || null;
      await routesAPI.create({
        name: formData.name,
        route_date: formData.route_date,
        driver_id: driverId,
        notes: formData.notes || null,
        status: 'planned',
      });
      toast.success('Route created successfully');
      setShowAddDialog(false);
      resetForm();
      fetchRoutes();
    } catch (error) {
      console.error('Error creating route:', error);
      toast.error('Failed to create route');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      route_date: getTodayString(),
      driver_id: 'unassigned',
      notes: '',
    });
  };

  const openAddDialog = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const hasRecommendations = recommendations?.recommendations?.length > 0;

  return (
    <AdminLayout title="Routes">
      {/* Smart Recommendations Banner */}
      {hasRecommendations && (
        <Card className="mb-6 border-[#52B8E1] bg-gradient-to-r from-[#52B8E1]/10 to-[#194461]/10">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-[#194461]">
                <Sparkles className="text-[#52B8E1]" size={20} />
                Smart Route Recommendations
                <Badge variant="secondary" className="ml-2 bg-[#52B8E1]/20 text-[#194461]">
                  {recommendations.recommendations.length} routes for tomorrow
                </Badge>
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowRecommendations(!showRecommendations)}
                >
                  {showRecommendations ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  {showRecommendations ? 'Hide' : 'Show'}
                </Button>
                {recommendations.recommendations.length > 1 && (
                  <Button 
                    size="sm"
                    className="bg-[#194461] hover:bg-[#091D2E]"
                    onClick={handleAcceptAll}
                    data-testid="accept-all-btn"
                  >
                    <CheckCircle size={14} className="mr-1" />
                    Accept All
                  </Button>
                )}
              </div>
            </div>
            <p className="text-sm text-slate-600 mt-1">
              Based on {recommendations.summary?.overdue_units_included || 0} overdue units and scheduled jobs
              {recommendations.summary?.average_efficiency && (
                <span className="ml-2">
                  • Avg efficiency: <span className="font-semibold text-[#194461]">{recommendations.summary.average_efficiency}%</span>
                </span>
              )}
            </p>
          </CardHeader>
          
          {showRecommendations && (
            <CardContent className="pt-2">
              {/* Unavailable Drivers Notice */}
              {recommendations.unavailable_drivers?.length > 0 && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-800 text-sm">
                    <UserX size={16} />
                    <span className="font-medium">Drivers off on {formatDate(recommendations.generated_for_date)}:</span>
                    {recommendations.unavailable_drivers.map((d, i) => (
                      <Badge key={i} variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                        {d.full_name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-2">
                {recommendations.recommendations.map((rec, idx) => {
                  const isEditing = editingRecIdx === idx;
                  
                  return (
                  <Card key={idx} className={`border-slate-200 bg-white ${isEditing ? 'ring-2 ring-[#52B8E1]' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-slate-900">{rec.name}</h4>
                          <p className="text-sm text-slate-500">{formatDate(rec.route_date)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Edit Toggle Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-7 w-7 p-0 ${isEditing ? 'text-[#52B8E1] bg-[#52B8E1]/10' : 'text-slate-400'}`}
                            onClick={() => setEditingRecIdx(isEditing ? null : idx)}
                            data-testid={`edit-rec-${idx}`}
                          >
                            {isEditing ? <X size={14} /> : <Edit2 size={14} />}
                          </Button>
                          {/* Efficiency Score Badge */}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge 
                                  className={`${
                                    rec.efficiency_score?.overall >= 80 ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
                                    rec.efficiency_score?.overall >= 60 ? 'bg-amber-100 text-amber-700 border-amber-300' :
                                    'bg-red-100 text-red-700 border-red-300'
                                  }`}
                                  variant="outline"
                                >
                                  <Gauge size={12} className="mr-1" />
                                  {rec.efficiency_score?.overall || 0}%
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <div className="space-y-1 text-xs">
                                  <p className="font-semibold">Efficiency Breakdown:</p>
                                  <p>Territory Match: {rec.efficiency_score?.territory_match}%</p>
                                  <p>Urgency Coverage: {rec.efficiency_score?.urgency_coverage}%</p>
                                  <p>Clustering: {rec.efficiency_score?.clustering}%</p>
                                  {rec.efficiency_score?.breakdown?.clustering_detail && (
                                    <p className="text-slate-500 mt-1">{rec.efficiency_score.breakdown.clustering_detail}</p>
                                  )}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <Badge className="bg-slate-100 text-slate-700">
                            {rec.total_stops} stops
                          </Badge>
                        </div>
                      </div>

                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Users size={14} className="text-slate-400" />
                          <span className="font-medium">{rec.driver_name || 'Unassigned'}</span>
                          {rec.driver_territory && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                              {rec.driver_territory} driver
                            </Badge>
                          )}
                        </div>
                        {rec.driver_territory !== rec.territory && rec.driver_territory && (
                          <div className="flex items-center gap-2 text-amber-600 text-xs">
                            <AlertTriangle size={12} />
                            <span>Driver's usual territory differs from route area</span>
                          </div>
                        )}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger className="flex items-center gap-2 text-slate-600">
                              <Clock size={14} className="text-slate-400" />
                              <span className="font-medium">{rec.estimated_time?.formatted || rec.estimated_duration || 'N/A'}</span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <div className="space-y-1 text-xs">
                                <p className="font-semibold">Time Breakdown:</p>
                                {rec.estimated_time?.work_minutes && (
                                  <p>Work time: {rec.estimated_time.work_minutes} min</p>
                                )}
                                {rec.estimated_time?.drive_minutes && (
                                  <p>Drive time: {rec.estimated_time.drive_minutes} min</p>
                                )}
                                <p className="text-slate-400 mt-1">Based on {rec.total_stops} stops</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>

                      {/* Stops Preview / Edit Mode */}
                      <div className={`bg-slate-50 rounded-lg p-3 mb-4 ${isEditing ? 'max-h-64' : 'max-h-32'} overflow-y-auto`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-slate-500">
                            {isEditing ? 'Edit Stops (click to remove):' : 'Stops:'}
                          </p>
                          {isEditing && rec.stops.length > 1 && (
                            <p className="text-xs text-[#52B8E1]">Click scissors to split here</p>
                          )}
                        </div>
                        {(isEditing ? rec.stops : rec.stops.slice(0, 4)).map((stop, stopIdx) => (
                          <div 
                            key={stopIdx} 
                            className={`flex items-start gap-2 text-xs text-slate-600 mb-1 ${isEditing ? 'group hover:bg-slate-100 rounded p-1 -mx-1' : ''}`}
                          >
                            <span className="text-slate-400 font-mono">{stopIdx + 1}.</span>
                            <div className="flex-1">
                              <span className="font-medium">{stop.customer_name}</span>
                              {stop.urgency_reason?.includes('OVERDUE') && (
                                <Badge variant="destructive" className="ml-1 text-[10px] px-1 py-0">
                                  OVERDUE
                                </Badge>
                              )}
                              <div className="flex items-center gap-2">
                                <span className="text-slate-400">{stop.job_type}</span>
                                {stop.units_requested > 1 && (
                                  <span className="text-slate-400">• {stop.units_requested} units</span>
                                )}
                                {stop.estimated_minutes && (
                                  <span className="text-slate-400">• ~{stop.estimated_minutes}min</span>
                                )}
                              </div>
                            </div>
                            {isEditing && (
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {stopIdx < rec.stops.length - 1 && rec.stops.length > 1 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                    onClick={() => splitRecommendation(idx, stopIdx)}
                                    title="Split route after this stop"
                                    data-testid={`split-after-${idx}-${stopIdx}`}
                                  >
                                    <Scissors size={12} />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => removeStopFromRecommendation(idx, stopIdx)}
                                  title="Remove this stop"
                                  data-testid={`remove-stop-${idx}-${stopIdx}`}
                                >
                                  <Trash2 size={12} />
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                        {!isEditing && rec.stops.length > 4 && (
                          <p className="text-xs text-slate-400 mt-1">
                            +{rec.stops.length - 4} more stops...
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button 
                          className="flex-1 bg-[#194461] hover:bg-[#091D2E]"
                          onClick={() => handleAcceptRecommendation(rec)}
                          disabled={acceptingRoute === rec.driver_id || rec.stops.length === 0}
                          data-testid={`accept-route-${idx}`}
                        >
                          {acceptingRoute === rec.driver_id ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                          ) : (
                            <CheckCircle size={14} className="mr-1" />
                          )}
                          Accept Route
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )})}
              </div>

              {recommendations.unassigned_stops?.length > 0 && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-800 text-sm">
                    <AlertTriangle size={16} />
                    <span className="font-medium">
                      {recommendations.unassigned_stops.length} stops couldn't be assigned
                    </span>
                    <span className="text-amber-600">- No available drivers</span>
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Loading recommendations */}
      {loadingRecs && !recommendations && (
        <Card className="mb-6 border-slate-200">
          <CardContent className="p-6 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-[#194461] mr-2" />
            <span className="text-slate-600">Generating smart recommendations...</span>
          </CardContent>
        </Card>
      )}

      {/* Filters & Add Button */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6 justify-between">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-slate-400" />
            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-auto"
              data-testid="date-filter"
            />
          </div>
          <Button 
            variant="outline" 
            onClick={() => setDateFilter(getTodayString())}
            data-testid="today-btn"
          >
            Today
          </Button>
          <Button 
            variant="outline"
            onClick={() => {
              setDateFilter(getTomorrowString());
              fetchRecommendations(getTomorrowString());
            }}
            data-testid="tomorrow-btn"
          >
            Tomorrow
          </Button>
          <Button 
            variant="ghost" 
            onClick={() => setDateFilter('')}
            data-testid="all-dates-btn"
          >
            All Dates
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            onClick={() => fetchRecommendations(dateFilter || getTomorrowString())}
            disabled={loadingRecs}
            data-testid="refresh-recs-btn"
          >
            <Sparkles size={16} className="mr-2" />
            {loadingRecs ? 'Generating...' : 'Get Recommendations'}
          </Button>
          <Button 
            onClick={openAddDialog}
            className="bg-[#194461] hover:bg-[#091D2E]"
            data-testid="add-route-btn"
          >
            <Plus size={18} className="mr-2" />
            Create Route
          </Button>
        </div>
      </div>

      {/* Routes List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#194461]" />
        </div>
      ) : routes.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Truck className="text-slate-400" size={32} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Routes Found</h3>
            <p className="text-slate-600 mb-4">
              {dateFilter ? `No routes scheduled for ${formatDate(dateFilter)}` : 'No routes found'}
            </p>
            {!hasRecommendations && (
              <Button 
                variant="outline"
                onClick={() => fetchRecommendations(dateFilter || getTomorrowString())}
              >
                <Sparkles size={16} className="mr-2" />
                Generate Smart Recommendations
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {routes.map((route) => (
            <Link 
              key={route.id} 
              to={`/admin/routes/${route.id}`}
              data-testid={`route-card-${route.id}`}
            >
              <Card className="border-slate-200 hover:border-[#52B8E1] hover:shadow-md transition-all h-full">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">{route.name}</h3>
                      <p className="text-sm text-slate-500">{formatDate(route.route_date)}</p>
                    </div>
                    <Badge className={getStatusColor(route.status)} variant="outline">
                      {route.status}
                    </Badge>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Users size={16} className="text-slate-400" />
                      <span>{route.driver_name || 'Unassigned'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Truck size={16} className="text-slate-400" />
                      <span>{route.job_count} stops</span>
                    </div>
                    {route.estimated_time?.formatted && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock size={16} className="text-slate-400" />
                        <span>{route.estimated_time.formatted}</span>
                      </div>
                    )}
                  </div>

                  {route.notes && (
                    <p className="mt-3 text-xs text-slate-500 bg-slate-50 p-2 rounded">
                      {route.notes}
                    </p>
                  )}

                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-end">
                    <span className="text-sm text-[#194461] font-medium flex items-center gap-1">
                      View Details <ArrowRight size={14} />
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Add Route Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Route</DialogTitle>
            <DialogDescription>
              Create a new service route for a specific day
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="route_name">Route Name *</Label>
              <Input
                id="route_name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Route 4 – Downtown Area"
                data-testid="input-route-name"
              />
            </div>

            <div>
              <Label htmlFor="route_date">Date *</Label>
              <Input
                id="route_date"
                type="date"
                value={formData.route_date}
                onChange={(e) => setFormData(prev => ({ ...prev, route_date: e.target.value }))}
                data-testid="input-route-date"
              />
            </div>

            <div>
              <Label htmlFor="driver">Assign Driver (Optional)</Label>
              <Select 
                value={formData.driver_id} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, driver_id: value }))}
              >
                <SelectTrigger data-testid="select-driver">
                  <SelectValue placeholder="Select a driver" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {drivers.map(driver => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.full_name}
                      {driver.territory && ` (${driver.territory})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any notes for this route..."
                rows={2}
                data-testid="input-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateRoute}
              disabled={saving || !formData.name || !formData.route_date}
              className="bg-[#194461] hover:bg-[#091D2E]"
              data-testid="save-route-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Route
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
